import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProfileModal.css';
import Panel from './Panel';
import ConfirmModal from './ConfirmModal';
import DeleteAccountModal from './DeleteAccountModal';
import ChangeEmailModal from './ChangeEmailModal';
import ActivityModal from './ActivityModal';
import ChangePasswordModal from './ChangePasswordModal';
import ChangeCarTypeModal from './ChangeCarTypeModal'; // Import the new modal
import Avatar from './Avatar';
import BlockedUsersList from './BlockedUsersList';
import { carSizeLabel } from '../utils/carSize';

// Below this many ratings the average is noise, so the profile shows a dash instead of a number.
// The same constant, for the same reason, as UserDetails.js in the mobile app.
const RATING_MIN_COUNT = 3;

const ProfileModal = ({ open, onClose, userData, currentUserId, addNotification, onCarDetailsUpdated, onProfileUpdate, onOpenOps, onLogout, fitsMyCarOnly, onFitsMyCarOnlyChange, selectedRadius, onRadiusChange, activity, activitySeenAt, hasUnreadActivity, onActivitySeen }) => {
  // 2026-10-28: two dialogs where "Update email/password" was one that saved nothing.
  const [credentialsDialog, setCredentialsDialog] = useState(null); // 'email' | 'password' | null
  const [showActivity, setShowActivity] = useState(false);
  const [showChangeCarTypeModal, setShowChangeCarTypeModal] = useState(false); // New state
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
  // Which view the panel is showing. Blocked users drills in HERE rather than opening a second
  // panel over this one — same as Messages opening a conversation. See BlockedUsersList.js.
  const [view, setView] = useState('profile');
  // What the slider SHOWS while it is being dragged. onRadiusChange — which refetches — is only
  // called when the drag ends, so this has to live here rather than being read straight off the
  // committed prop.
  const [radiusDraft, setRadiusDraft] = useState(selectedRadius);
  useEffect(() => { setRadiusDraft(selectedRadius); }, [selectedRadius]);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Basic validation
    if (!file.type.startsWith('image/')) {
      addNotification('Please select an image file.', 'red');
      return;
    }

    const formData = new FormData();
    formData.append('avatar', file);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch('http://localhost:3001/api/users/avatar', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        addNotification('Avatar updated successfully!', 'green');
        if (onProfileUpdate) {
          onProfileUpdate();
        }
      } else {
        addNotification('Failed to update avatar.', 'red');
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      addNotification('An error occurred during upload.', 'red');
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Which field the car editor opens on: the row that was clicked.
  const [carEditorField, setCarEditorField] = useState(null);
  const openCarEditor = (field) => {
    setCarEditorField(field);
    setShowChangeCarTypeModal(true);
  };

  const handleCloseChangeCarTypeModal = () => { // New handler
    setShowChangeCarTypeModal(false);
  };


  // 2026-10-15: was a centred modal over a scrim, headed by the Udal logo and wordmark — a dialog
  // you dismiss. A profile is a place you browse, and on the phone it is a tab. It is the shared
  // Panel now, titled by what it IS rather than by the product's name, which the bar three
  // centimetres above it is already saying.
  //
  // The two sub-modals below stay modals on purpose: changing your password or your car is a task
  // with an outcome, which is exactly what a dialog is for.
  return (
    <>
    <Panel
      open={open}
      onClose={onClose}
      title={view === 'blocked' ? 'Blocked users' : 'Profile'}
      onBack={view === 'blocked' ? () => setView('profile') : undefined}
      labelledBy="profile-panel-title"
    >
      <div className="profile-modal-body">
          {view === 'blocked' ? (
            <BlockedUsersList />
          ) : userData ? (
            <> {/* Start of React.Fragment */}
              {/* 2026-10-15: rebuilt for a 340px panel. The old layout was two columns beside a
                  100px avatar and a stats grid, designed for a 500px modal — in the panel the
                  values wrapped onto three lines, "Rank: top %" lost its number off the edge, and
                  the whole thing grew a horizontal scrollbar. A narrow column wants one column. */}
              <div className="profile-identity">
                <div className="user-avatar-only" onClick={handleAvatarClick} title="Change your picture">
                  <Avatar username={userData.username} avatarUrl={userData.avatar_url} size={64} />
                  <div className="avatar-edit-overlay">Edit</div>
                </div>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleFileChange} />
                <div className="profile-identity-text">
                  <div className="profile-name">{userData.username}</div>
                  <div className="profile-since">
                    Member since {new Date(userData.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="panel-section-title">Your car</div>
              {/* 2026-10-28: each row opens the car editor at that field, as tapping a row does on the
                  phone. They were read-only facts, editable only through a separate "Car details"
                  item further down, which is gone with this. */}
              <div className="profile-facts profile-facts--editable">
                {[
                  ['plate', 'Plate', userData.plate_number ? userData.plate_number.toUpperCase() : '—'],
                  ['color', 'Colour', userData.car_color || '—'],
                  ['size', 'Size', carSizeLabel(userData.car_type) || '—'],
                ].map(([field, label, value]) => (
                  <button key={field} type="button" className="profile-fact-row" onClick={() => openCarEditor(field)}>
                    <span className="profile-fact-label">{label}</span>
                    <span className="profile-fact-value">{value}</span>
                    <span className="menu-item-arrow">&rsaquo;</span>
                  </button>
                ))}
              </div>

              {/* The phone's strip, with the phone's numbers. 2026-10-16: this was a list of
                  Spots declared / Spots taken / Average arrival / Rating / Rank — the very set the
                  mobile app retired, and for reasons that apply here just as much:

                    Declared -> HELPED   spots_declared counts every park the ENGINE detects, so
                                         with automatic detection on it measured how much you
                                         drive and presented it as generosity. Helped counts
                                         handovers somebody actually completed.
                    Taken    -> SHARED   "taken" counted spaces you took FROM other people, which
                                         the words did not say. Shared counts spaces you made
                                         available.
                    (none)   -> RETURNS  the times Udal took you back to your own car — the only
                                         number here that says the app was USED, not merely
                                         running. The web never showed it at all.
                    Rank                 was a percentile that gave full marks for never having
                                         completed a handover. Gone from both clients.

                  Average arrival is dropped rather than moved: it is a number about the OTHER
                  side's punctuality and it meant nothing on your own profile. */}
              <div className="profile-stats">
                <div className="profile-stat">
                  <span className="profile-stat-number">{userData.drivers_helped ?? 0}</span>
                  <span className="profile-stat-label">Helped</span>
                </div>
                <div className="profile-stat">
                  <span className="profile-stat-number">{userData.spots_shared ?? 0}</span>
                  <span className="profile-stat-label">Shared</span>
                </div>
                <div className="profile-stat">
                  <span className="profile-stat-number">{userData.navigations ?? 0}</span>
                  <span className="profile-stat-label">Returns</span>
                </div>
                <div className="profile-stat">
                  {/* An average of one rating is not an average. Below RATING_MIN_COUNT it stays a
                      dash rather than letting a single grumpy stranger define somebody's profile —
                      the web was showing "4.0 / 5 · 1 ratings" where the phone showed "—". */}
                  <span className="profile-stat-number">
                    {(userData.rating_count ?? 0) >= RATING_MIN_COUNT && userData.rating != null
                      ? Number(userData.rating).toFixed(1)
                      : '—'}
                  </span>
                  <span className="profile-stat-label">Rating</span>
                </div>
              </div>

              {/* 2026-10-17: these two were behind a "Map & filters" row that opened a dialog. The
                  phone keeps them inline under PREFERENCES and so does this now — they are two
                  controls, and a dialog to hold two controls is a door to a cupboard. */}
              <div className="panel-section-title">Preferences</div>
              <label className="profile-pref">
                <span className="profile-pref-label">Only spots that fit my car</span>
                <span className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={!!fitsMyCarOnly}
                    onChange={(e) => onFitsMyCarOnlyChange(e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </span>
              </label>
              {/* A slider, like the phone's, and for the phone's reason: a radius is a magnitude,
                  and the five values the dropdown offered — 2, 5, 10, 20, 30 — were arbitrary.
                  Same range as the phone, so the two clients mean the same thing by it: 200 m to
                  1 km since 2026-10-28 (it was 1-10 km, which is half an hour of city traffic).

                  The number is shown beside the label because a slider on its own says "more" and
                  "less" but never "how much", and this one decides what you are shown. */}
              <div className="profile-pref profile-pref--slider">
                <label className="profile-pref-label" htmlFor="radius-slider">
                  Spot search radius
                  <span className="profile-pref-value">
                    {radiusDraft >= 1 ? `${radiusDraft} km` : `${Math.round(radiusDraft * 1000)} m`}
                  </span>
                </label>
                <input
                  id="radius-slider"
                  type="range"
                  min="0.2"
                  max="1"
                  step="0.1"
                  className="profile-pref-range"
                  value={radiusDraft}
                  // Moves the number as you drag; does NOT refetch. The commit below is on
                  // release, the same split the phone makes — widening the radius refetches, and
                  // doing that at every step of a drag is nine requests to reach the tenth.
                  onChange={(e) => setRadiusDraft(Number(e.target.value))}
                  onPointerUp={() => onRadiusChange(radiusDraft)}
                  onKeyUp={() => onRadiusChange(radiusDraft)}
                  onBlur={() => onRadiusChange(radiusDraft)}
                />
              </div>

              <div className="panel-section-title">Account</div>
              {/* 2026-10-28: what used to be the log along the bottom of the map. The dot: something
                  new since you last looked. */}
              <button type="button" className="profile-menu-item" onClick={() => setShowActivity(true)}>
                <span>Activity</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {hasUnreadActivity && <span aria-label="New activity" style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff3b30' }} />}
                  <span className="menu-item-arrow">&rsaquo;</span>
                </span>
              </button>
              <button type="button" className="profile-menu-item" onClick={() => setCredentialsDialog('email')}>
                <span>Email</span><span className="menu-item-arrow">&rsaquo;</span>
              </button>
              <button type="button" className="profile-menu-item" onClick={() => setCredentialsDialog('password')}>
                <span>Change password</span><span className="menu-item-arrow">&rsaquo;</span>
              </button>
              {/* 2026-10-15: moved here from the hamburger, which is gone. A menu holding three
                  unrelated things — a settings dialog, an admin page and log out — was a drawer for
                  whatever had nowhere else to live. All three are about YOU, so they live with you. */}
              {onOpenOps && (
                <button type="button" className="profile-menu-item" onClick={() => { onClose(); onOpenOps(); }}>
                  <span>Ops Centre</span><span className="menu-item-arrow">&rsaquo;</span>
                </button>
              )}
              {/* 2026-10-17: the phone has had this list since blocking shipped; the web had no way
                  to see it. The block itself was always enforced server-side for both clients, so
                  this adds the review and the undo, not the protection. */}
              {/* A route, not a drill-in like Blocked users below: it is a map plus a long list,
                  which a 340px panel cannot hold. See ParkingHistory.js. */}
              <button type="button" className="profile-menu-item" onClick={() => navigate('/history')}>
                <span>Parking history</span><span className="menu-item-arrow">&rsaquo;</span>
              </button>
              <button type="button" className="profile-menu-item" onClick={() => setView('blocked')}>
                <span>Blocked users</span><span className="menu-item-arrow">&rsaquo;</span>
              </button>
              {/* The same hosted documents the mobile app and App Store Connect point at — one copy,
                  one address, nothing to keep in step. */}
              <button type="button" className="profile-menu-item" onClick={() => window.open('/terms', '_blank', 'noopener')}>
                <span>Terms of use</span><span className="menu-item-arrow">&rsaquo;</span>
              </button>
              <button type="button" className="profile-menu-item" onClick={() => window.open('/privacy', '_blank', 'noopener')}>
                <span>Privacy policy</span><span className="menu-item-arrow">&rsaquo;</span>
              </button>
              {onLogout && (
                <button type="button" className="profile-menu-item is-danger" onClick={() => setConfirmLogout(true)}>
                  <span>Log out</span><span className="menu-item-arrow">&rsaquo;</span>
                </button>
              )}
              {/* Last, and on its own: the phone puts it at the foot of the screen for the same
                  reason. It is not a setting, and it should not sit in a list people scroll
                  through looking for one. */}
              {onLogout && (
                <button
                  type="button"
                  // Not is-danger: see the note in ProfileModal.css. The red belongs to the
                  // dialog this opens, not to the row that opens it.
                  className="profile-menu-item profile-delete-account"
                  onClick={() => setConfirmDeleteAccount(true)}
                >
                  <span>Delete my account</span>
                </button>
              )}

            </> /* End of React.Fragment */
          ) : (
            <p>Loading profile data...</p>
          )}
      </div>
    </Panel>
      {/* Logging out is one tap from a list people scroll past, and it ends the session — the one
          action in here with a cost and no undo. */}
      <ConfirmModal
        isOpen={confirmLogout}
        title="Log out?"
        message="You will need to sign in again to see your car or the spaces around you."
        confirmLabel="Log out"
        tone="danger"
        onConfirm={() => { setConfirmLogout(false); onLogout(); }}
        onClose={() => setConfirmLogout(false)}
      />
      <DeleteAccountModal
        isOpen={confirmDeleteAccount}
        username={userData?.username}
        onClose={() => setConfirmDeleteAccount(false)}
        addNotification={addNotification}
        // The account no longer exists, so the local session cannot refer to anything. onLogout is
        // the same path the Log out row takes, which is the only sane destination from here.
        onDeleted={() => { setConfirmDeleteAccount(false); onLogout(); }}
      />
      {showActivity && (
        <ActivityModal
          entries={activity}
          seenAt={activitySeenAt}
          onSeen={onActivitySeen}
          onClose={() => setShowActivity(false)}
        />
      )}
      {credentialsDialog === 'email' && (
        <ChangeEmailModal
          onClose={() => setCredentialsDialog(null)}
          userData={userData}
          addNotification={addNotification}
          // Reload the profile, so the new address is what the next dialog shows.
          onChanged={() => onProfileUpdate?.()}
        />
      )}
      {credentialsDialog === 'password' && (
        <ChangePasswordModal
          onClose={() => setCredentialsDialog(null)}
          currentUserId={currentUserId}
          userData={userData}
          addNotification={addNotification}
        />
      )}
      {showChangeCarTypeModal && <ChangeCarTypeModal
        onClose={handleCloseChangeCarTypeModal}
        currentUserId={currentUserId}
        userData={userData}
        addNotification={addNotification}
        onCarDetailsUpdated={onCarDetailsUpdated}
        focusField={carEditorField}
      />}
    </>
  );
};

export default ProfileModal;